import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adminExists, setAdminExists] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "Site Admin"
  });

  useEffect(() => {
    checkAdminExists();
    checkIfLoggedIn();
  }, []);

  const checkAdminExists = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      setAdminExists(response.data.exists);
    } catch (error) {
      console.error("Failed to check admin status:", error);
    }
  };

  const checkIfLoggedIn = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) return;
      
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        navigate("/admin");
      }
    } catch (error) {
      // Not logged in, stay on login page
      localStorage.removeItem("admin_token");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (adminExists === false) {
        // Create admin account
        await axios.post(`${API}/admin/setup`, {
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
        toast.success("Admin account created!");
      }

      // Login
      const response = await axios.post(
        `${API}/admin/login`,
        {
          email: formData.email,
          password: formData.password
        }
      );

      localStorage.setItem("admin_token", response.data.token);
      toast.success("Welcome to Admin Dashboard!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  if (adminExists === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/20 rounded-sm flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-serif tracking-tight">360Lumina Admin</h1>
          <p className="text-muted-foreground mt-2">
            {adminExists === false ? "Create your admin account" : "Sign in to admin dashboard"}
          </p>
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-serif">
              {adminExists === false ? "Setup Admin" : "Admin Login"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {adminExists === false && (
                <div>
                  <Label htmlFor="name">Admin Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Site Admin"
                    className="mt-2"
                    data-testid="admin-name"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@lumina.com"
                  required
                  className="mt-2"
                  data-testid="admin-email"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    required
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary rounded-sm"
                disabled={loading}
                data-testid="admin-submit"
              >
                {loading ? (
                  <div className="spinner w-4 h-4" />
                ) : adminExists === false ? (
                  "Create Admin & Login"
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-8">
          <a href="/" className="text-primary hover:underline">
            Back to 360Lumina
          </a>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
